import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Check, Loader2, Pencil, X } from 'lucide-react';
import { klarysUrl } from './klarysApi';

const STATUT = {
  programme: ['Programmé', 'bg-violet-100 text-violet-800'],
  en_cours: ['En cours d\'envoi', 'bg-sky-100 text-sky-800'],
  execute: ['Envoyé', 'bg-emerald-100 text-emerald-800'],
  partiel: ['Envoi incomplet', 'bg-amber-100 text-amber-800'],
  echec: ['Échec', 'bg-rose-100 text-rose-800'],
  annule: ['Annulé', 'bg-slate-100 text-slate-600'],
};
const paris = (iso) => new Date(iso).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'medium', timeStyle: 'short' });
const versChamp = (iso) => new Date(iso).toLocaleString('sv-SE', { timeZone: 'Europe/Paris' }).slice(0, 16).replace(' ', 'T');

const Ligne = ({ e, headers, onReload }) => {
  const [edition, setEdition] = useState(false);
  const [valeur, setValeur] = useState(versChamp(e.programme_le));
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATUT[e.statut] || STATUT.programme;
  const agir = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setEdition(false); onReload(); } catch (err) { toast.error(err.response?.data?.detail || 'Action impossible'); } finally { setBusy(false); }
  };
  const modifiable = e.statut === 'programme';
  return (
    <li className="rounded-xl border border-violet-100 bg-white p-3 text-sm" data-testid={`klarys-ep-${e.id}`}>
      <div className="flex items-start gap-2">
        <CalendarClock className="mt-0.5 h-4 w-4 text-violet-600" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">{e.titre}</p>
          <p className="text-xs text-slate-600">{e.nb_destinataires} destinataire(s){e.critere_label ? ` · ${e.critere_label}` : ''}{e.par ? ` · par ${e.par}` : ''}</p>
          {edition ? (
            <div className="mt-1.5 flex items-center gap-1.5">
              <input type="datetime-local" value={valeur} min={versChamp(new Date(Date.now() + 5 * 60000).toISOString())} onChange={(ev) => setValeur(ev.target.value)} className="rounded border border-slate-300 px-2 py-1 text-xs" data-testid={`klarys-ep-date-${e.id}`} />
              <span className="text-[11px] text-slate-500">heure de Paris</span>
              <button type="button" disabled={busy} onClick={() => agir(() => axios.patch(klarysUrl(`/envois-programmes/${e.id}`), { programme_le: valeur }, { headers }), 'Date modifiée')}
                className="rounded-md bg-violet-600 p-1 text-white" data-testid={`klarys-ep-enregistrer-${e.id}`}><Check className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => setEdition(false)} className="rounded-md border p-1 text-slate-500" data-testid={`klarys-ep-fermer-${e.id}`}><X className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <p className="mt-1 text-[11px] text-slate-500" data-testid={`klarys-ep-infos-${e.id}`}>
              {modifiable || e.statut === 'en_cours' ? <>Prévu le <b>{paris(e.programme_le)}</b> (Paris)</>
                : e.statut === 'annule' ? <>Annulé le <b>{paris(e.annule_le || e.programme_le)}</b>{e.annule_par ? ` par ${e.annule_par}` : ''} — était prévu le {paris(e.programme_le)}</>
                  : <>Exécuté le <b>{paris(e.execute_le || e.programme_le)}</b> (Paris)</>}{e.resultat ? ` — ${e.resultat}` : ''}
            </p>
          )}
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`} data-testid={`klarys-ep-statut-${e.id}`}>{label}</span>
        {modifiable && !edition && (
          <div className="flex gap-1">
            <button type="button" title="Changer la date" onClick={() => setEdition(true)} className="rounded-md border border-slate-200 p-1.5 text-violet-700 hover:bg-violet-50" data-testid={`klarys-ep-modifier-${e.id}`}><Pencil className="h-3.5 w-3.5" /></button>
            <button type="button" title="Annuler l'envoi" disabled={busy}
              onClick={() => window.confirm(`Annuler l'envoi programmé « ${e.titre} » ?`) && agir(() => axios.delete(klarysUrl(`/envois-programmes/${e.id}`), { headers }), 'Envoi annulé')}
              className="rounded-md border border-slate-200 p-1.5 text-rose-600 hover:bg-rose-50" data-testid={`klarys-ep-annuler-${e.id}`}><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
    </li>
  );
};

export const EnvoisProgrammes = ({ headers }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => axios.get(klarysUrl('/envois-programmes'), { headers }).then((r) => setItems(r.data.items)).catch(() => setItems([])), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  return (
    <section className="mb-5" data-testid="klarys-envois-programmes">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Envois groupés programmés</h3>
      {!items ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        : !items.length ? <p className="text-sm text-slate-500" data-testid="klarys-ep-vide">Aucun envoi programmé. Exemple : « envoie un rappel aux impayés DOS-DOMIUM de plus de 30 jours lundi à 9 h ».</p>
          : <ul className="space-y-2">{items.map((e) => <Ligne key={e.id} e={e} headers={headers} onReload={load} />)}</ul>}
    </section>
  );
};
